// Mietstände eines Mietverhältnisses (mieter.miet_historie): jede Mietänderung
// ist ein eigener Stand mit „gültig ab" — die vorherige Miete bleibt erhalten und
// sichtbar. Reine Funktionen, damit die Regeln testbar sind; die Datenbank-Zugriffe
// liegen im Modal.
//
// Kein „gültig bis" im Datensatz: Der Zeitraum ergibt sich aus dem nächsten Stand.
// Damit kann weder eine Lücke noch eine Überschneidung entstehen.

export type PositionArt = 'stellplatz' | 'garage' | 'keller' | 'raum' | 'sonstiges';

export interface KostenPosition {
  id: string;
  art: PositionArt;
  /** Bei „raum" das Wo, bei „sonstiges" der freie Text; sonst optionaler Zusatz. */
  bezeichnung?: string | null;
  betrag: number;
}

export interface Mietstand {
  id: string;
  /** ISO-Datum (YYYY-MM-DD), ab wann dieser Stand gilt. */
  gueltigAb: string;
  kaltmiete: number;
  betriebskosten: number;
  heizkosten: number;
  /** Summe der Positionen; bleibt für den Altbestand ohne Positionen lesbar. */
  weitereKosten: number;
  positionen?: KostenPosition[];
  notiz?: string | null;
}

export const POSITION_ARTEN: { wert: PositionArt; label: string; brauchtBezeichnung: boolean }[] = [
  { wert: 'stellplatz', label: 'Stellplatz', brauchtBezeichnung: false },
  { wert: 'garage', label: 'Garage', brauchtBezeichnung: false },
  { wert: 'keller', label: 'Keller', brauchtBezeichnung: false },
  { wert: 'raum', label: 'Zusätzlicher Raum', brauchtBezeichnung: true },
  { wert: 'sonstiges', label: 'Sonstiges', brauchtBezeichnung: true },
];

const zahl = (v: unknown): number => {
  const n = typeof v === 'number' ? v : parseFloat(String(v ?? '').replace(',', '.'));
  return Number.isFinite(n) ? n : 0;
};

const artLabel = (art: PositionArt): string =>
  POSITION_ARTEN.find((a) => a.wert === art)?.label || 'Sonstiges';

/** „Stellplatz", „Zusätzlicher Raum · Dachgeschoss links", bei Sonstiges nur der Text. */
export function positionLabel(p: Pick<KostenPosition, 'art' | 'bezeichnung'>): string {
  const bez = (p.bezeichnung || '').trim();
  if (p.art === 'sonstiges') return bez || 'Sonstiges';
  return bez ? `${artLabel(p.art)} · ${bez}` : artLabel(p.art);
}

/** Pflichtprüfung einer Position; null = in Ordnung. */
export function positionFehler(p: Pick<KostenPosition, 'art' | 'bezeichnung' | 'betrag'>): string | null {
  const braucht = POSITION_ARTEN.find((a) => a.wert === p.art)?.brauchtBezeichnung;
  if (braucht && !(p.bezeichnung || '').trim()) {
    return p.art === 'raum' ? 'Bitte angeben, um welchen Raum es geht.' : 'Bitte eine Bezeichnung angeben.';
  }
  if (zahl(p.betrag) < 0) return 'Der Betrag darf nicht negativ sein.';
  return null;
}

export function summePositionen(positionen: KostenPosition[] | undefined | null): number {
  return (positionen || []).reduce((s, p) => s + zahl(p.betrag), 0);
}

export function gesamtmiete(stand: Pick<Mietstand, 'kaltmiete' | 'betriebskosten' | 'heizkosten' | 'weitereKosten'>): number {
  return zahl(stand.kaltmiete) + zahl(stand.betriebskosten) + zahl(stand.heizkosten) + zahl(stand.weitereKosten);
}

/** Aufsteigend nach „gültig ab"; Stände ohne Datum ans Ende (Eingabefehler sollen auffallen). */
export function sortiereStaende<T extends Pick<Mietstand, 'gueltigAb'>>(staende: T[]): T[] {
  return [...staende].sort((a, b) => {
    if (!a.gueltigAb) return 1;
    if (!b.gueltigAb) return -1;
    return a.gueltigAb.localeCompare(b.gueltigAb);
  });
}

/**
 * Der zum Stichtag gültige Stand: der jüngste mit gueltigAb <= heute.
 * Liegt jeder Stand in der Zukunft, gilt der früheste — sonst stünde die Miete
 * bis zum Stichtag auf 0.
 */
export function aktuellerStand<T extends Pick<Mietstand, 'gueltigAb'>>(staende: T[], heuteIso: string): T | null {
  const sortiert = sortiereStaende(staende);
  if (sortiert.length === 0) return null;
  let treffer: T | null = null;
  for (const s of sortiert) {
    if (s.gueltigAb && s.gueltigAb <= heuteIso) treffer = s;
  }
  return treffer ?? sortiert[0];
}

const deDatum = (iso: string): string => {
  const [j, m, t] = iso.split('-');
  return t && m && j ? `${t}.${m}.${j}` : iso;
};

/** Vortag als ISO-Datum — das „bis" eines Standes ist immer der Tag vor dem nächsten. */
export function vortag(iso: string): string {
  const d = new Date(`${iso}T12:00:00Z`);
  d.setUTCDate(d.getUTCDate() - 1);
  return d.toISOString().slice(0, 10);
}

/**
 * Zeitraum-Beschriftung eines Standes innerhalb der sortierten Liste:
 * „1.9.2017 – 31.7.2026" für abgelöste Stände, „ab 1.8.2026" für den letzten.
 */
export function zeitraumLabel(staende: Pick<Mietstand, 'gueltigAb'>[], index: number): string {
  const sortiert = sortiereStaende(staende);
  const s = sortiert[index];
  if (!s?.gueltigAb) return '—';
  const naechster = sortiert[index + 1];
  if (!naechster?.gueltigAb) return `ab ${deDatum(s.gueltigAb)}`;
  return `${deDatum(s.gueltigAb)} – ${deDatum(vortag(naechster.gueltigAb))}`;
}

/** Prüft einen Stand vor dem Übernehmen; `andere` sind die übrigen Stände. */
export function standFehler(
  stand: Pick<Mietstand, 'gueltigAb'>, andere: Pick<Mietstand, 'gueltigAb'>[],
): string | null {
  if (!stand.gueltigAb) return 'Bitte angeben, ab wann die Änderung gilt.';
  if (andere.some((a) => a.gueltigAb === stand.gueltigAb)) return 'Für dieses Datum gibt es bereits einen Mietstand.';
  return null;
}

/** Die vier Mieter-Felder aus einem Stand — sie bleiben das Spiegelbild des aktuellen Standes. */
export function spiegelFelder(stand: Mietstand): {
  kaltmiete: number; nebenkosten_vorauszahlung: number; heizkosten: number; weitere_kosten: number;
} {
  return {
    kaltmiete: zahl(stand.kaltmiete),
    nebenkosten_vorauszahlung: zahl(stand.betriebskosten),
    heizkosten: zahl(stand.heizkosten),
    weitere_kosten: zahl(stand.weitereKosten),
  };
}

/** Normalisiert einen (auch alten) Eintrag aus miet_historie. */
export function leseStand(roh: any, index = 0): Mietstand {
  const positionen: KostenPosition[] = Array.isArray(roh?.positionen)
    ? roh.positionen.map((p: any, i: number) => ({
        id: String(p?.id ?? `p-${index}-${i}`),
        art: (POSITION_ARTEN.some((a) => a.wert === p?.art) ? p.art : 'sonstiges') as PositionArt,
        bezeichnung: p?.bezeichnung ?? null,
        betrag: zahl(p?.betrag),
      }))
    : [];
  return {
    id: String(roh?.id ?? `m-${index}`),
    gueltigAb: typeof roh?.gueltigAb === 'string' ? roh.gueltigAb.slice(0, 10) : '',
    kaltmiete: zahl(roh?.kaltmiete),
    betriebskosten: zahl(roh?.betriebskosten),
    heizkosten: zahl(roh?.heizkosten),
    // Altbestand hat nur weitereKosten, neue Stände führen zusätzlich die Positionen.
    weitereKosten: positionen.length ? summePositionen(positionen) : zahl(roh?.weitereKosten),
    positionen,
    notiz: roh?.notiz ?? null,
  };
}

/**
 * Ergänzt den Anfangsstand aus den heutigen Mieter-Feldern, wenn die Historie leer
 * ist oder keinen Stand zum Mietbeginn hat. Idempotent — mehrfaches Anwenden ändert
 * nichts mehr.
 */
export function mitAnfangsstand(
  staende: Mietstand[],
  werte: { kaltmiete?: unknown; nebenkosten_vorauszahlung?: unknown; heizkosten?: unknown; weitere_kosten?: unknown },
  startIso: string,
): Mietstand[] {
  const start = (startIso || '').slice(0, 10);
  if (!start) return sortiereStaende(staende);
  if (staende.some((s) => s.gueltigAb === start)) return sortiereStaende(staende);
  // Nur ergänzen, wenn kein früherer Stand existiert — sonst wäre der Anfang schon erfasst.
  if (staende.some((s) => s.gueltigAb && s.gueltigAb <= start)) return sortiereStaende(staende);
  const anfang: Mietstand = {
    id: `m-anfang-${start}`,
    gueltigAb: start,
    kaltmiete: zahl(werte.kaltmiete),
    betriebskosten: zahl(werte.nebenkosten_vorauszahlung),
    heizkosten: zahl(werte.heizkosten),
    weitereKosten: zahl(werte.weitere_kosten),
    positionen: [],
    notiz: null,
  };
  return sortiereStaende([anfang, ...staende]);
}
